import { useState, useRef } from "react";
import { Button, StatusChip, Text } from "@fragments";
import { DeleteRounded, FileCopyRounded } from "@mui/icons-material";
import * as Material from "@mui/material";
import {
  AssetType,
  ASSET_TYPE_CONFIG,
  buildAssetHint,
  getAssetAccept,
  validateAssetFile,
} from "../../constants/assetTypes";
import { useUploadCommunityAsset } from "../../hooks/useUploadCommunityAsset";
import "./styles.scss";

/**
 * File drop zone + presigned S3 upload for a community asset category.
 *
 * @param {{
 *   communityId: number|string|null|undefined,
 *   category: string,
 *   path?: string | null,
 *   onPathChange: (url: string | undefined) => void,
 *   onUploadingChange?: (uploading: boolean) => void,
 *   assetType?: string,
 *   accept?: string,
 *   maxBytes?: number,
 *   hint?: string,
 *   formatBytes?: (n: number) => string,
 * }} props
 */
export default function FileDropAssetField({
  communityId,
  category,
  path,
  onPathChange,
  onUploadingChange,
  assetType = AssetType.PDF,
  accept: acceptOverride,
  maxBytes = 100 * 1024 * 1024,
  hint: hintOverride,
  formatBytes,
}) {
  const fileInputRef = useRef(null);
  const dragDepthRef = useRef(0);
  const [pendingName, setPendingName] = useState(null);
  const [pendingSize, setPendingSize] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [dragOver, setDragOver] = useState(false);

  const { uploadFile } = useUploadCommunityAsset({
    communityId,
    onUploadingChange,
  });

  const fmt =
    formatBytes ||
    ((bytes) => {
      if (!bytes && bytes !== 0) return "";
      const units = ["B", "KB", "MB", "GB"];
      let size = bytes;
      let i = 0;
      while (size >= 1024 && i < units.length - 1) {
        size /= 1024;
        i += 1;
      }
      return `${size.toFixed(size >= 100 ? 0 : size >= 10 ? 1 : 2)} ${units[i]}`;
    });

  const accept = acceptOverride ?? getAssetAccept(assetType);
  const hint =
    hintOverride ?? buildAssetHint(assetType, maxBytes, formatBytes || fmt);

  const typeUi =
    ASSET_TYPE_CONFIG[assetType] ?? ASSET_TYPE_CONFIG[AssetType.PDF];
  const StatusChipIcon = typeUi.StatusChipIcon;

  const hasServerPath = !!path && path.startsWith("http");

  const notifyValidation = (message) => {
    if (typeof window.setSnackbar === "function") {
      window.setSnackbar(message);
    } else {
      window.alert(message);
    }
  };

  const handleSelectedFile = async (file) => {
    if (!file) return;
    const check = validateAssetFile(file, assetType, maxBytes, formatBytes || fmt);
    if (!check.valid) {
      notifyValidation(check.message);
      return;
    }

    setPendingName(file.name);
    setPendingSize(file.size);
    setUploading(true);
    setDragOver(false);
    dragDepthRef.current = 0;
    try {
      const url = await uploadFile(file, category);
      onPathChange(url);
    } catch (e) {
      console.error(e);
      setPendingName(null);
      setPendingSize(null);
      window.setSnackbar?.("Não foi possível enviar o arquivo. Tente novamente.");
    } finally {
      setUploading(false);
    }
  };

  const handleInputChange = (e) => {
    const file = e?.target?.files?.[0];
    handleSelectedFile(file);
    if (e?.target) e.target.value = null;
  };

  const handleDrop = (e) => {
    e.preventDefault();
    dragDepthRef.current = 0;
    setDragOver(false);
    const file = e.dataTransfer?.files?.[0];
    handleSelectedFile(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    dragDepthRef.current += 1;
    setDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    dragDepthRef.current -= 1;
    if (dragDepthRef.current <= 0) {
      dragDepthRef.current = 0;
      setDragOver(false);
    }
  };

  const openFilePicker = () => {
    fileInputRef.current?.click();
  };

  const clearPending = () => {
    setPendingName(null);
    setPendingSize(null);
    onPathChange(undefined);
  };

  const removeExisting = () => {
    onPathChange(undefined);
  };

  if (hasServerPath && !pendingName) {
    const fileName = path.split("/").pop() || "Arquivo existente";
    return (
      <div className="file-drop-field__existing-file">
        <div className="file-drop-field__existing-file-main">
          <FileCopyRounded className="file-drop-field__existing-file-icon" />
          <div className="file-drop-field__existing-file-name">
            <Text.span maxLines={1} grow>
              {fileName}
            </Text.span>
          </div>
        </div>
        <div className="file-drop-field__existing-file-actions">
          <Button
            color="overlay"
            onClick={removeExisting}
            icon={<DeleteRounded />}
          />
        </div>
      </div>
    );
  }

  const showUploadingRow = uploading && pendingName;
  const showFileInfo = !uploading && pendingName && path;

  if (showUploadingRow) {
    return (
      <div className="file-drop-field__existing-file file-drop-field__existing-file--uploading mt-2">
        <div className="file-drop-field__existing-file-main">
          <FileCopyRounded className="file-drop-field__existing-file-icon" />
          <div className="file-drop-field__uploading-row">
            <Material.CircularProgress
              className="file-drop-field__uploading-spinner"
              size={18}
              thickness={4}
            />
            <div className="file-drop-field__existing-file-name file-drop-field__truncate-name">
              <Text.h5 weight={500} loading>
                {pendingName}
              </Text.h5>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="file-asset-field">
      {!showFileInfo ? (
        <div
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragEnter={handleDragEnter}
          onDragLeave={handleDragLeave}
          onClick={openFilePicker}
          className={`file-drop-field__dropzone mt-2${
            dragOver ? " file-drop-field__dropzone--drag-over" : ""
          }`}
        >
          <div className="file-drop-field__dropzone-default">
            <Text.h5 weight={500}>{typeUi.dragLine}</Text.h5>

            <input
              ref={fileInputRef}
              type="file"
              accept={accept}
              onChange={handleInputChange}
              className="file-drop-field__input"
              disabled={uploading}
            />

            <Button
              variant="contained"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                openFilePicker();
              }}
              className="file-drop-field__button"
              disabled={uploading}
            >
              Selecionar arquivo
            </Button>

            <Text.span contrast={6}>{hint}</Text.span>
          </div>
        </div>
      ) : null}

      {showFileInfo ? (
        <div className="file-drop-field__file-info">
          <FileCopyRounded />

          <div className="file-drop-field__file-details">
            <Text.h5 weight={500} maxLines={1}>
              {pendingName}
            </Text.h5>
            <div className="file-drop-field__file-details-size">
              <StatusChip.Default
                className="file-drop-field__file-details-chip"
                icon={<StatusChipIcon />}
                text={typeUi.statusChipText}
              />
              <Text.span style={{ opacity: 0.5 }}>{fmt(pendingSize)}</Text.span>
            </div>


          </div>
          <Button
            onClick={clearPending}
            color="overlay"
            icon={<DeleteRounded />}
            tooltip="Remover arquivo"
          />
        </div>
      ) : null}
    </div>
  );
}
